import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';
import '../i18n';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../lib/utils';
import { BETA_APPLICATION_MESSAGE, BETA_APPLICATION_SUBJECT } from '../lib/beta';

function FeedbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, getToken } = useAuth();
  const [subject, setSubject] = useState(location.state?.subject || '');
  const [message, setMessage] = useState(location.state?.message || '');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (message.length < 50) {
      setError(t('feedback.messageTooShort'));
      return;
    }

    if (message.length > 2500) {
      setError(t('feedback.messageTooLong'));
      return;
    }

    setLoading(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        headers['Authorization'] = `Bearer ${getToken()}`;
      }

      const body = { subject, message };
      if (!isAuthenticated) {
        body.contactEmail = contactEmail;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('feedback.failedToSubmit'));
      }

      setSuccess(true);
      setSubject('');
      setMessage('');
      setContactEmail('');

      if (isAuthenticated) {
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {isAuthenticated && (
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('feedback.back')}
          </button>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg shadow-sm p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t('feedback.title')}</h1>
          <p className="text-muted-foreground">
            {t('feedback.intro')}
          </p>
        </div>

        {isAuthenticated && !success && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-start gap-3">
            <Rocket aria-hidden="true" className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground">
                Want to help shape Laftel Lingo? Apply to join our beta program and get a year of
                Premium free.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubject(BETA_APPLICATION_SUBJECT);
                  setMessage(BETA_APPLICATION_MESSAGE);
                }}
                className="text-sm font-semibold text-primary hover:underline mt-1"
              >
                Fill in a beta application
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">
                {isAuthenticated ? t('feedback.submittedRedirecting') : t('feedback.submittedThanks')}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isAuthenticated && (
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-foreground mb-2">
                  {t('feedback.yourEmail')}
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  placeholder={t('auth.login.emailPlaceholder')}
                  className="w-full px-4 py-2 bg-secondary/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            )}

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                {t('feedback.subject')}
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder={t('feedback.subjectPlaceholder')}
                className="w-full px-4 py-2 bg-secondary/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="message" className="block text-sm font-medium text-foreground">
                  {t('feedback.message')}
                </label>
                <span className={`text-xs ${message.length < 50 ? 'text-destructive' : message.length > 2500 ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {t('feedback.charCount', { count: message.length })} {message.length < 50 && t('feedback.minCount')}
                </span>
              </div>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                minLength={50}
                maxLength={2500}
                rows={8}
                placeholder={t('feedback.messagePlaceholder')}
                className="w-full px-4 py-2 bg-secondary/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('feedback.submitting') : t('feedback.submit')}
              </button>
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  className="px-6 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-secondary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default FeedbackPage;
